import axios from "axios";
import { adminAuth } from "@/lib/api";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/university`;

const baseClient = axios.create({ baseURL: API_BASE, timeout: 30000 });

const adminClient = axios.create({ baseURL: API_BASE, timeout: 30000 });
adminClient.interceptors.request.use((config) => {
  const token = adminAuth.getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const universityApi = {
  // Public
  options: () => baseClient.get("/options"),
  submit: (payload) => baseClient.post("/applications", payload),
  startCheckout: (applicationId, body) =>
    baseClient.post(`/applications/${applicationId}/checkout`, body),
  paymentStatus: (applicationId, params) =>
    baseClient.get(`/applications/${applicationId}/payment-status`, { params }),
  status: (application_id, email) =>
    baseClient.post("/applications/status", { application_id, email }),

  // Admin
  adminHealth: () => adminClient.get("/admin/health"),
  adminList: (params) => adminClient.get("/admin/applications", { params }),
  adminDetail: (id) => adminClient.get(`/admin/applications/${id}`),
  adminDecide: (id, body) => adminClient.post(`/admin/applications/${id}/decision`, body),
  adminConvertToEnrollment: (id) =>
    adminClient.post(`/admin/applications/${id}/convert-to-enrollment`),
  adminUpdatePayment: (id, body) => adminClient.post(`/admin/applications/${id}/payment`, body),
  adminUpdateNotes: (id, admissions_notes) =>
    adminClient.patch(`/admin/applications/${id}/notes`, { admissions_notes }),
  adminGetCatalog: () => adminClient.get("/admin/catalog"),
  adminPutCatalog: (catalog) => adminClient.put("/admin/catalog", catalog),
  adminPatchProgram: (division_key, level_key, patch) =>
    adminClient.patch("/admin/catalog/program", { division_key, level_key, patch }),

  // Enrollment — public
  enrollmentCreate: (body) => baseClient.post("/enrollments", body),
  enrollmentByEmail: (email) => baseClient.get(`/enrollments/by-email/${encodeURIComponent(email)}`),
  enrollmentGet: (id) => baseClient.get(`/enrollments/${id}`),
  enrollmentAddCourse: (id, body) => baseClient.post(`/enrollments/${id}/courses`, body),
  enrollmentProgress: (id) => baseClient.get(`/enrollments/${id}/progress`),
  enrollmentTuitionCheckout: (id) => baseClient.get(`/enrollments/${id}/tuition-checkout`),
  studentTranscript: (email) => baseClient.get(`/students/${encodeURIComponent(email)}/transcript`),

  // Enrollment — admin
  adminEnrollmentList: (params) => adminClient.get("/admin/enrollments", { params }),
  adminEnrollmentCreate: (body) => adminClient.post("/admin/enrollments", body),
  adminEnrollmentPatch: (id, body) => adminClient.patch(`/admin/enrollments/${id}`, body),
  adminCourseCompletion: (id, body) => adminClient.post(`/admin/enrollments/${id}/course-completion`, body),

  // LMS — public
  lmsModules: (courseCode, enrollmentId) => baseClient.get(`/lms/courses/${encodeURIComponent(courseCode)}/modules`, { params: { enrollment_id: enrollmentId } }),
  lmsLesson: (lessonId, enrollmentId) => baseClient.get(`/lms/lessons/${lessonId}`, { params: { enrollment_id: enrollmentId } }),
  lmsLessonComplete: (lessonId, enrollmentId, body) => baseClient.post(`/lms/lessons/${lessonId}/complete`, body || {}, { params: { enrollment_id: enrollmentId } }),
  lmsQuiz: (quizId, enrollmentId) => baseClient.get(`/lms/quizzes/${quizId}`, { params: { enrollment_id: enrollmentId } }),
  lmsQuizSubmit: (quizId, enrollmentId, answers) => baseClient.post(`/lms/quizzes/${quizId}/attempt`, { answers }, { params: { enrollment_id: enrollmentId } }),
  lmsCertificates: (email) => baseClient.get(`/lms/certificates/by-email/${encodeURIComponent(email)}`),
  lmsCertificatePdfUrl: (certId) => `${API_BASE}/lms/certificates/${certId}/pdf`,

  // LMS — admin
  lmsAdminModules: (courseCode) => adminClient.get(`/lms/admin/courses/${encodeURIComponent(courseCode)}/modules`),
  lmsAdminCreateModule: (body) => adminClient.post(`/lms/admin/modules`, body),
  lmsAdminPatchModule: (id, body) => adminClient.patch(`/lms/admin/modules/${id}`, body),
  lmsAdminDeleteModule: (id) => adminClient.delete(`/lms/admin/modules/${id}`),
  lmsAdminCreateLesson: (body) => adminClient.post(`/lms/admin/lessons`, body),
  lmsAdminPatchLesson: (id, body) => adminClient.patch(`/lms/admin/lessons/${id}`, body),
  lmsAdminDeleteLesson: (id) => adminClient.delete(`/lms/admin/lessons/${id}`),
  lmsAdminCreateQuiz: (body) => adminClient.post(`/lms/admin/quizzes`, body),
  lmsAdminPatchQuiz: (id, body) => adminClient.patch(`/lms/admin/quizzes/${id}`, body),
  lmsAdminDeleteQuiz: (id) => adminClient.delete(`/lms/admin/quizzes/${id}`),
  lmsAdminSeedDefaults: () => adminClient.post(`/lms/admin/seed-defaults`),
  lmsAdminIssueCert: (enrollmentId) => adminClient.post(`/lms/admin/certificates/issue`, { enrollment_id: enrollmentId }),

  // Assignments — public (student-facing)
  assignmentsListByCourse: (courseCode, enrollmentId) =>
    baseClient.get(`/lms/assignments/${encodeURIComponent(courseCode)}`, { params: { enrollment_id: enrollmentId } }),
  assignmentGet: (assignmentId, enrollmentId) =>
    baseClient.get(`/lms/assignments/by-id/${assignmentId}`, { params: { enrollment_id: enrollmentId } }),
  assignmentSubmit: (assignmentId, enrollmentId, body) =>
    baseClient.post(`/lms/assignments/${assignmentId}/submit`, body, { params: { enrollment_id: enrollmentId } }),

  // Assignments — admin
  assignmentsAdminList: (courseCode) =>
    adminClient.get(`/lms/admin/assignments/${encodeURIComponent(courseCode)}`),
  assignmentAdminCreate: (body) => adminClient.post(`/lms/admin/assignments`, body),
  assignmentAdminPatch: (id, body) => adminClient.patch(`/lms/admin/assignments/${id}`, body),
  assignmentAdminDelete: (id) => adminClient.delete(`/lms/admin/assignments/${id}`),
  submissionsAdminList: (params) => adminClient.get(`/lms/admin/submissions`, { params }),
  submissionAdminGet: (id) => adminClient.get(`/lms/admin/submissions/${id}`),
  submissionAdminGrade: (id, body) => adminClient.post(`/lms/admin/submissions/${id}/grade`, body),

  // Transcript PDF (public; URL by email)
  transcriptPdfUrl: (email) => `${API_BASE}/lms/students/${encodeURIComponent(email)}/transcript-pdf`,

  // Final Exams — student
  examEligibility: (enrollmentId) => baseClient.get(`/lms/final-exams/${enrollmentId}/eligibility`),
  examStart: (enrollmentId) => baseClient.post(`/lms/final-exams/${enrollmentId}/start`),
  examAttemptGet: (attemptId) => baseClient.get(`/lms/final-exams/attempts/${attemptId}`),
  examAttemptAutosave: (attemptId, body) => baseClient.patch(`/lms/final-exams/attempts/${attemptId}`, body),
  examAttemptSubmit: (attemptId, body) => baseClient.post(`/lms/final-exams/attempts/${attemptId}/submit`, body || {}),
  graduationStatus: (enrollmentId) => baseClient.get(`/lms/graduation/${enrollmentId}`),

  // Final Exams — admin
  examQuestionsList: (params) => adminClient.get(`/lms/admin/exam-questions`, { params }),
  examQuestionCreate: (body) => adminClient.post(`/lms/admin/exam-questions`, body),
  examQuestionPatch: (id, body) => adminClient.patch(`/lms/admin/exam-questions/${id}`, body),
  examQuestionDelete: (id) => adminClient.delete(`/lms/admin/exam-questions/${id}`),
  examQuestionsBulk: (items) => adminClient.post(`/lms/admin/exam-questions/bulk`, { items }),
  examConfigsList: () => adminClient.get(`/lms/admin/final-exams/configs`),
  examConfigUpsert: (levelKey, body) => adminClient.put(`/lms/admin/final-exams/configs/${levelKey}`, body),
  examConfigsSeedDefaults: () => adminClient.post(`/lms/admin/final-exams/configs/seed-defaults`),
  examAttemptsList: (params) => adminClient.get(`/lms/admin/final-exams/attempts`, { params }),
  examAttemptOverride: (id, body) => adminClient.post(`/lms/admin/final-exams/attempts/${id}/override`, body),
  dtsPortfolios: () => adminClient.get(`/lms/admin/dts/portfolios`),
  dtsApprove: (enrollmentId, body) => adminClient.post(`/lms/admin/dts/${enrollmentId}/approve`, body),

  // Events (CE + Residency unified) — public
  eventsList: (params) => baseClient.get(`/events`, { params }),
  eventGet: (id) => baseClient.get(`/events/${id}`),
  eventRegister: (id, body) => baseClient.post(`/events/${id}/register`, body),
  eventCheckout: (id, registrationId) => baseClient.get(`/events/${id}/checkout`, { params: { registration_id: registrationId } }),
  eventMyRegistrations: (email) => baseClient.get(`/events/registrations/by-email/${encodeURIComponent(email)}`),
  residencyCasesByEmail: (email) => baseClient.get(`/events/residency-cases/by-email/${encodeURIComponent(email)}`),

  // Events — admin
  eventsAdminList: (params) => adminClient.get(`/events/admin/list`, { params }),
  eventsAdminCreate: (body) => adminClient.post(`/events/admin`, body),
  eventsAdminPatch: (id, body) => adminClient.patch(`/events/admin/${id}`, body),
  eventsAdminDelete: (id) => adminClient.delete(`/events/admin/${id}`),
  eventsAdminRegistrations: (id) => adminClient.get(`/events/admin/${id}/registrations`),
  eventsAdminMarkAttended: (regId) => adminClient.post(`/events/admin/registrations/${regId}/mark-attended`),
  eventsAdminIssueCert: (regId) => adminClient.post(`/events/admin/registrations/${regId}/issue-certificate`),
  eventsAdminSeedDefaults: () => adminClient.post(`/events/admin/seed-defaults`),
  eventsAdminCreateCase: (body) => adminClient.post(`/events/admin/residency-cases`, body),
  eventsAdminPatchCase: (id, body) => adminClient.patch(`/events/admin/residency-cases/${id}`, body),
  eventsAdminCasesByRegistration: (regId) => adminClient.get(`/events/admin/residency-cases/by-registration/${regId}`),

  // ── Question Bank — admin ──────────────────────────────────────────────
  qbTemplateUrl: (course_code = "", fmt = "csv") =>
    `${API_BASE}/qb/admin/template.${fmt}?course_code=${encodeURIComponent(course_code)}`,
  qbPreview: (formData) =>
    adminClient.post(`/qb/admin/preview`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
  qbCommit: (jobId, admin_email) =>
    adminClient.post(`/qb/admin/import/${jobId}`, null, { params: { admin_email } }),
  qbList: (params) => adminClient.get(`/qb/admin/questions`, { params }),
  qbCreate: (body, admin_email) => adminClient.post(`/qb/admin/questions`, body, { params: { admin_email } }),
  qbPatch: (qid, body) => adminClient.patch(`/qb/admin/questions/${qid}`, body),
  qbDelete: (qid) => adminClient.delete(`/qb/admin/questions/${qid}`),
  qbJobs: () => adminClient.get(`/qb/admin/jobs`),

  // ── Grading — admin ────────────────────────────────────────────────────
  gradingStudents: (params) => adminClient.get(`/grading/admin/students`, { params }),
  gradingOverview: (studentId) => adminClient.get(`/grading/admin/student/${encodeURIComponent(studentId)}/overview`),
  gradingSubmissions: (studentId, course_code = "") =>
    adminClient.get(`/grading/admin/student/${encodeURIComponent(studentId)}/submissions`, { params: { course_code } }),
  gradingCreateSubmission: (body) => adminClient.post(`/grading/admin/submissions`, body),
  gradingPatchStatus: (sid, status) => adminClient.patch(`/grading/admin/submissions/${sid}/status`, { status }),
  gradingUpsertGrade: (body) => adminClient.post(`/grading/admin/grade`, body),
  gradingGetGrade: (sid) => adminClient.get(`/grading/admin/grade/${sid}`),

  // ── Feedback — admin ───────────────────────────────────────────────────
  feedbackUpsert: (body) => adminClient.post(`/grading/admin/feedback`, body),
  feedbackAIDraft: (body) => adminClient.post(`/grading/admin/feedback/ai-draft`, body),
  feedbackPublish: (body) => adminClient.post(`/grading/admin/feedback/publish`, body),
  feedbackReturnRevision: (body) => adminClient.post(`/grading/admin/feedback/return-revision`, body),

  // ── Feedback — student-facing (public, published only) ─────────────────
  studentPublishedFeedback: (studentId) =>
    baseClient.get(`/grading/student/${encodeURIComponent(studentId)}/feedback`),

  // ── Transcripts — admin ────────────────────────────────────────────────
  transcriptJson: (studentId) => adminClient.get(`/transcripts/admin/${encodeURIComponent(studentId)}`),
  transcriptCsvUrl: (studentId) => `${API_BASE}/transcripts/admin/${encodeURIComponent(studentId)}.csv`,
  transcriptPdfUrl: (studentId) => `${API_BASE}/transcripts/admin/${encodeURIComponent(studentId)}.pdf`,
  transcriptEmail: (studentId, requested_by) =>
    adminClient.post(`/transcripts/admin/${encodeURIComponent(studentId)}/email`, { requested_by }),
};
